import styled from '@xstyled/styled-components';
import React from 'react';
import { jt, t } from 'ttag';

const Numbers = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 16px;
  margin-bottom: 8px;

  strong {
    font-family: ralewayBold;
    font-weight: 400;
    font-size: 22px;
  }
`;

const Track = styled.div`
  position: relative;
  height: 10px;
  border-radius: 999px;
  background-color: var(--color-grey-lt);
  border: 1px solid var(--color-grey-md);
`;

const Fill = styled.span<{ passed: boolean }>`
  position: absolute;
  top: -1px;
  bottom: -1px;
  left: -1px;
  border-radius: inherit;
  background-color: ${p =>
    p.passed ? 'var(--color-green-dk)' : 'var(--color-accent-md)'};
`;

const Tick = styled.span`
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 2px;
  margin-left: -1px;
  border-radius: 2px;
  background-color: var(--color-black);
`;

const TickLabel = styled.span`
  position: absolute;
  top: 10px;
  font-size: 13px;
  white-space: nowrap;
`;

/**
 * The learner's score, as the server recorded it, against the passing score when the assessment has one. The
 * bar is a picture of the numbers, which are also in its label and written out beside it. Under the strict
 * rule there is no passing score, so the bar shows the score alone, with no tick and no pass colouring.
 */
export const PretestScoreMeter = ({
  scorePercent,
  passingScorePercent,
  correctCount,
  questionCount
}: {
  scorePercent: number;
  passingScorePercent: number | null;
  correctCount: number | null;
  questionCount: number;
}) => {
  const passed =
    passingScorePercent != null && scorePercent >= passingScorePercent;
  // One message holds the score, so each language orders it and writes the percent its own way.
  const score = <strong key="score">{scorePercent}</strong>;

  return (
    <div>
      <Numbers>
        <span>{jt`Your score ${score}%`}</span>
        {correctCount != null && (
          <span>{t`${correctCount} of ${questionCount} correct`}</span>
        )}
      </Numbers>
      <Track
        role="img"
        aria-label={
          passingScorePercent == null
            ? t`Your score ${scorePercent}%.`
            : t`Your score ${scorePercent}%. Passing score ${passingScorePercent}%.`
        }
      >
        <Fill
          passed={passed}
          style={{ width: `calc(${scorePercent}% + 2px)` }}
        />
        {passingScorePercent != null && (
          <Tick style={{ left: `${passingScorePercent}%` }} />
        )}
      </Track>
      {/* Shifted by the share of its width that the tick sits at, so it stays inside the bar. */}
      {passingScorePercent != null && (
        <div aria-hidden="true" style={{ position: 'relative', height: 28 }}>
          <TickLabel
            style={{
              left: `${passingScorePercent}%`,
              transform: `translateX(-${passingScorePercent}%)`
            }}
          >
            {t`Passing score ${passingScorePercent}%`}
          </TickLabel>
        </div>
      )}
    </div>
  );
};
